import React, { useState, useRef} from 'react';
import ReactDOM from 'react-dom';
import { X, Check, XCircle } from 'lucide-react';

const QuizModal = ({ isLoading, questions, videoFileName, onClose, isIntroQuiz = false}) => {

  const [userAnswers, setUserAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const videoRef = useRef(null);

  const handleAnswerChange = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleClose = () => {
    // For intro quiz, require submission before closing
    if (isIntroQuiz && !submitted) {
      return;
    }
    setUserAnswers({});
    setSubmitted(false);
    setShowVideo(true);
    setQuizStarted(false);
    setVideoWatched(false);
    onClose();
  };

  const handleStartQuiz = () => {
    setShowVideo(false);
    setQuizStarted(true);
  };

  // Track when video has been watched (reached near the end)
  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      const { currentTime, duration } = videoRef.current;
      // Consider video "watched" when user reaches 90% or more
      if (duration > 0 && (currentTime / duration) >= 0.9) {
        setVideoWatched(true);
      }
    }
  };

  // Alternative: Track when video ends
  const handleVideoEnded = () => {
    setVideoWatched(true);
  };

  // Check if all questions are answered
  const allQuestionsAnswered = questions?.length > 0 && 
    Object.keys(userAnswers).length === questions.length;

  const modalContent = (
    <div className="modal-overlay-quiz">
      <div className="modal-quiz-wrapper">
        {/* Video Section - shown initially */}
        {showVideo && videoFileName && !isLoading && (
          <>
            <div className="video-container-quiz">
              <video 
                ref={videoRef}
                controls 
                className="video-player-quiz"
                key={videoFileName}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={handleVideoEnded}
              >
                <source src={videoFileName} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="video-footer-quiz">
              <button
                onClick={handleStartQuiz}
                disabled={!videoWatched}
                className="btn-start-quiz"
                style={{
                  opacity: videoWatched ? 1 : 0.5,
                  cursor: videoWatched ? 'pointer' : 'not-allowed'
                }}
              >
                {videoWatched ? 'Start Quiz' : 'Watch video to continue'}
              </button>
            </div>
          </>
        )}

        {/* Quiz Section - shown after video */}
        {(quizStarted || !videoFileName) && (
          <div className="modal-quiz">

        {/* Header */}
        <div className="modal-header-quiz">
          <h2 className="modal-title-quiz">Quiz</h2>
          {!isLoading && !isIntroQuiz && (
            <button
              onClick={handleClose}
              className="modal-close-button"
              aria-label="Close"
            >
              &times;
            </button>
          )}
        </div>

        {/* Content */}
        <div className="modal-content-quiz">
          {isLoading ? (
            <div className="modal-loading">
              <span className="loading-spinner medium"></span>
              <div style={{ color: "#405159" }}>Loading quiz...</div>
            </div>
          ) : (
            <>

              {/* Questions */}
              <div className="questions-container-quiz">
                {questions?.map((q, idx) => (
                  <div key={q.question_id} className="question-block-quiz">
                    <h3 className="question-text-quiz">
                      {idx + 1}. {q.question}
                    </h3>
                    
                    <div className="choices-container-quiz">
                      {['choice_a', 'choice_b', 'choice_c', 'choice_d'].map((choice) => {
                        const choiceLetter = choice.split('_')[1].toUpperCase();
                        const isSelected = userAnswers[q.question_id] === choiceLetter;
                        const isCorrect = q.correct_answer === choiceLetter;
                        const showResult = submitted;
                        
                        let className = 'choice-item-quiz';
                        if (showResult) {
                          if (isCorrect) {
                            className += ' choice-correct-quiz';
                          } else if (isSelected && !isCorrect) {
                            className += ' choice-incorrect-quiz';
                          }
                        } else if (isSelected) {
                          className += ' choice-selected-quiz';
                        }

                        return (
                          <label
                            key={choice}
                            className={className}
                          >
                            <input
                              type="radio"
                              name={`question-${q.question_id}`}
                              value={choiceLetter}
                              checked={isSelected}
                              onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                              disabled={submitted}
                              className="choice-radio-quiz"
                            />
                            <span className="choice-content-quiz">
                              <span className="choice-text-quiz">
                                <strong>{choiceLetter}.</strong> {q[choice]}
                              </span>
                              {showResult && isCorrect && (
                                <span className="choice-feedback-correct-quiz">
                                  <Check size={18} />
                                  Correct
                                </span>
                              )}
                              {showResult && isSelected && !isCorrect && (
                                <span className="choice-feedback-incorrect-quiz">
                                  <XCircle size={18} />
                                  Incorrect
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!isLoading && (
          <div className="modal-footer-quiz">
            {!submitted ? (
              <>
                {!isIntroQuiz && (
                  <button
                    onClick={handleClose}
                    className="btn-cancel-quiz"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={!allQuestionsAnswered}
                  className={`btn-submit-quiz ${!allQuestionsAnswered ? 'btn-disabled-quiz' : ''}`}
                >
                  Submit Answers
                </button>
              </>
            ) : (
              <>
              {isIntroQuiz && (
                 <span style={{ 
                    color: 'red', 
                    fontWeight: 'bold',
                    animation: 'blink 1s infinite',
                    marginRight: '16px'
                  }}>
                    Please check your answers before continuing
                 </span>
              )}

              <button
                onClick={handleClose}
                className="btn-submit-quiz"
              >
                {isIntroQuiz ? 'Continue to Modules' : 'Close'}
              </button>
            </>
            )}
          </div>
        )}
      </div>
         )
        };
      </div>
    </div>
  );

  // Use React Portal to render modal at root level
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default QuizModal;